import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import config from './config';

interface SessionData {
  sessionId: string;
  status: string;
  customerEmail: string;
  planName: string;
  paymentStatus: string;
}

export const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchSessionData = async () => {
      if (!sessionId) {
        setError('No session ID found');
        setLoading(false);
        return;
      }

      try {
        const url = `${config.apiBaseUrl}/api/stripe/checkout-session/${sessionId}`;
        console.log('Fetching session data from:', url);
        
        const response = await fetch(url);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Response status:', response.status, 'Body:', errorText);
          throw new Error(`Failed to fetch session data: ${response.status}`);
        }
        const data = await response.json();
        setSessionData(data);
      } catch (err) {
        console.error('Error fetching session:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-center text-gray-600">Processing your subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-red-600 text-4xl mb-4 text-center">❌</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4 text-center">Error</h1>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <button
            onClick={() => navigate('/billing')}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition"
          >
            Back to Billing
          </button>
        </div>
      </div>
    );
  }

  const isPaid = sessionData?.paymentStatus === 'paid';

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success Card */}
        <div className="bg-white rounded-lg shadow-xl p-8 mb-6">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4 animate-bounce">🎉</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {isPaid ? 'Subscription Activated!' : 'Subscription Created!'}
            </h1>
            <p className="text-gray-600">
              {isPaid
                ? 'Your payment has been processed successfully'
                : 'Thank you for your subscription'}
            </p>
          </div>

          {/* Plan Details */}
          {sessionData && (
            <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-600 text-sm font-semibold">Plan</p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {sessionData.planName}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm font-semibold">Email</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {sessionData.customerEmail}
                  </p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-indigo-200">
                <p className="text-gray-700">
                  <span className="font-semibold">Session ID:</span>{' '}
                  <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                    {sessionData.sessionId.substring(0, 20)}...
                  </code>
                </p>
              </div>
            </div>
          )}

          {/* Status Messages */}
          {isPaid ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <span className="text-2xl mr-3">✅</span>
                <div>
                  <h3 className="font-semibold text-green-800 mb-1">
                    Payment Confirmed
                  </h3>
                  <p className="text-green-700 text-sm">
                    Your account and all premium features are now fully activated.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <span className="text-2xl mr-3">⏳</span>
                <div>
                  <h3 className="font-semibold text-blue-800 mb-1">
                    Processing Your Payment
                  </h3>
                  <p className="text-blue-700 text-sm">
                    Your subscription has been created. Your account and premium features
                    will be fully activated once your payment is confirmed. This usually
                    takes just a few moments.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* What You Get */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              {isPaid ? '🎓 You Now Have Access To:' : '🎓 You Will Get Access To:'}
            </h3>
            <ul className="space-y-2">
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-2">✓</span>
                All premium courses and materials
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-2">✓</span>
                Unlimited practice tests
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-2">✓</span>
                Advanced performance analytics
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-2">✓</span>
                Personalized study recommendations
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-2">✓</span>
                Priority customer support
              </li>
            </ul>
          </div>

          {/* Email Confirmation */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 text-sm">
              📧 A confirmation email has been sent to <strong>{sessionData?.customerEmail}</strong>
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/practice')}
              className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-3 px-4 rounded-lg hover:from-indigo-700 hover:to-blue-700 transition font-semibold"
            >
              {isPaid ? '🚀 Start Learning Now' : '📚 Explore Courses'}
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gray-100 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-200 transition font-semibold"
            >
              Go to Dashboard
            </button>
            {isPaid && (
              <button
                onClick={() => navigate('/billing')}
                className="w-full bg-gray-50 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-100 transition font-semibold border border-gray-200"
              >
                Manage Subscription
              </button>
            )}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">❓ Questions?</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-800">When will my features activate?</h3>
              <p className="text-gray-600">
                {isPaid
                  ? 'Your premium features are now active! You can immediately access all your subscribed content.'
                  : 'Once your payment is confirmed (usually within minutes), all premium features will be activated automatically.'}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">Can I change my plan?</h3>
              <p className="text-gray-600">
                Yes! You can upgrade, downgrade, or cancel your subscription at any time from
                your billing settings.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">How do I get support?</h3>
              <p className="text-gray-600">
                Contact us at support@exammasters.com or visit the help section in your account
                dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
